/*
  Sticky add-to-cart bar (mobile).

  Deliberately does NOT own a form. Subscription apps installed as app embeds
  (RecurPay on this store) inject their inputs into the real product form at
  runtime, and commonly bind a click handler to the add-to-cart button itself to
  stamp the selected selling plan on submit. So this proxies by clicking the real
  submit button — form.requestSubmit() would fire the form's submit event but
  skip those button handlers, silently adding a one-time purchase instead of a
  subscription.

  Replaces the StickyBuyButton element previously in pdp.js, which only scrolled
  to the form rather than adding to cart.
*/

if (!customElements.get("sticky-atc")) {
  customElements.define(
    "sticky-atc",
    class StickyAtc extends HTMLElement {
      constructor() {
        super();

        this.activeClass = "sticky-atc--show";
        this.observer = null;
        this.widgetObserver = null;
        this.intersecting = [];
      }

      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.alwaysVisible = this.dataset.alwaysVisible === "true";

        this.productForm = document.getElementById(this.dataset.formId);
        if (!this.productForm) return;

        this.submitProxy = this.querySelector("[data-sticky-atc-submit]");
        this.submitProxyLabel = this.querySelector(
          "[data-sticky-atc-submit-label]",
        );
        this.spinner = this.querySelector(".loading-overlay__spinner");
        this.priceTarget = this.querySelector("[data-sticky-atc-price]");
        this.mediaTarget = this.querySelector("[data-sticky-atc-media]");
        this.purchaseType = this.querySelector("[data-sticky-atc-purchase-type]");
        this.tagTarget = this.querySelector("[data-sticky-atc-tag]");
        this.purchaseTypeLabel = this.querySelector(
          "[data-sticky-atc-purchase-type-label]",
        );
        this.quantityInput = this.querySelector(".js-counter-quantity");

        this.sellingPlans = this.parseSellingPlans();

        this.bindSubmit();
        this.bindQuantity();
        this.watchRealButton();
        this.watchVariantChange();
        this.watchPurchaseType();
        this.updatePrice();
        this.initVisibility();

        document.body.classList.add("sticky-atc-active");

        window.stickyAtcDebug = () => this.debug();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.widgetObserver?.disconnect();
        this.buttonObserver?.disconnect();
        clearInterval(this.pollTimer);
        document.body.classList.remove("sticky-atc-active");
      }

      /*
        Run window.stickyAtcDebug() in the console to see what the bar can and
        cannot find. Useful for pinning down subscription app markup.
      */
      debug() {
        const scan = (selector) =>
          Array.from(document.querySelectorAll(selector)).map((el) => ({
            tag: el.tagName.toLowerCase(),
            id: el.id || null,
            class: el.className || null,
            name: el.getAttribute("name"),
            value: el.value,
            checked: el.checked,
          }));

        return {
          formFound: !!this.productForm,
          formId: this.dataset.formId,
          realSubmitButton: this.realSubmitButton?.outerHTML.slice(0, 200),
          realQuantityInput: !!this.realQuantityInput,
          sellingPlanInputs: scan('[name="selling_plan"]'),
          currentSellingPlanId: this.currentSellingPlanId(),
          sellingPlanMapFromLiquid: this.sellingPlans,
          legacyPropertyInputs: scan('input[name^="properties"]'),
          widgetResolved: this.widget
            ? {
                tag: this.widget.tagName.toLowerCase(),
                id: this.widget.id || null,
                class: this.widget.className || null,
              }
            : null,
          recurpayLikeElements: scan(
            '[data-recurpay], [class*="recurpay" i], [id*="recurpay" i]',
          ),
          iframesOnPage: scan("iframe").map((f) => f.id || f.class),
          shadowRootHosts: Array.from(document.querySelectorAll("*"))
            .filter((el) => el.shadowRoot)
            .map((el) => el.tagName.toLowerCase()),
          purchaseTypeVisible: this.purchaseType
            ? !this.purchaseType.hidden
            : "setting disabled",
          purchaseTypeText: this.purchaseTypeLabel?.textContent.trim(),
        };
      }

      parseSellingPlans() {
        const node = this.querySelector("[data-sticky-atc-selling-plans]");
        if (!node) return {};
        try {
          return JSON.parse(node.textContent);
        } catch (e) {
          return {};
        }
      }

      /*
        The real submit button. Resolved lazily on every use rather than cached,
        because a subscription app embed may replace or swap it after load.
        Falls back to any visible submit in the form.
      */
      get realSubmitButton() {
        const byId = document.getElementById(
          `ProductSubmitButton-${this.sectionId}`,
        );
        if (byId && byId.offsetParent !== null) return byId;

        const candidates = Array.from(
          this.productForm.querySelectorAll('[name="add"], [type="submit"]'),
        );
        return candidates.find((btn) => btn.offsetParent !== null) || byId;
      }

      get realQuantityInput() {
        return this.productForm.querySelector('[name="quantity"]');
      }

      bindSubmit() {
        this.submitProxy?.addEventListener("click", (event) => {
          event.preventDefault();

          /* Guard against double taps — product-form.js also bails, but this
             stops the second tap re-entering the loading state. */
          if (this.submitProxy.classList.contains("loading")) return;

          const button = this.realSubmitButton;
          if (!button || button.hasAttribute("disabled")) return;

          this.trackClick();
          this.setLoading(true);
          button.click();
        });
      }

      trackClick() {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "sticky_atc_click",
          product_id: this.productForm.querySelector('[name="id"]')?.value,
          quantity: this.quantityInput ? this.quantityInput.value : "1",
          selling_plan: this.currentSellingPlanId() || "",
        });
      }

      /* Mirror the bar's quantity onto the real form so the two can't disagree */
      bindQuantity() {
        if (!this.quantityInput) return;

        const source = this.realQuantityInput;

        /*
          No quantity input on the form means the buy_buttons block has its
          quantity selector switched off. Hide ours rather than show a stepper
          that silently does nothing.
        */
        if (!source) {
          this.querySelector(".sticky-atc__quantity")?.remove();
          this.quantityInput = null;
          return;
        }

        this.quantityInput.addEventListener("change", () => {
          source.value = this.quantityInput.value;
          source.dispatchEvent(new Event("change", { bubbles: true }));
        });

        source.addEventListener("change", () => {
          if (this.quantityInput.value !== source.value) {
            this.quantityInput.value = source.value;
          }
        });
      }

      /*
        Loading and disabled state are read off the real button rather than
        recomputed, so app-driven changes stay in sync. Observe the whole form
        rather than the button, and re-resolve the button on each callback — an
        app embed can replace the button node, which would otherwise leave the
        observer bound to a detached element.
      */
      watchRealButton() {
        this.syncDisabled(this.realSubmitButton);

        this.buttonObserver = new MutationObserver(() => {
          const button = this.realSubmitButton;
          if (!button) return;
          this.syncDisabled(button);
          if (!button.classList.contains("loading")) this.setLoading(false);
        });

        this.buttonObserver.observe(this.productForm, {
          attributes: true,
          attributeFilter: ["disabled", "class", "aria-disabled"],
          subtree: true,
          childList: true,
        });
      }

      syncDisabled(button) {
        if (!button) return;

        const disabled = button.hasAttribute("disabled");
        this.submitProxy?.toggleAttribute("disabled", disabled);

        const realLabel = button.querySelector("span");
        if (realLabel && this.submitProxyLabel) {
          const text = realLabel.textContent.trim();
          if (text) this.submitProxyLabel.textContent = text;
        }
      }

      setLoading(isLoading) {
        this.submitProxy?.classList.toggle("loading", isLoading);
        this.spinner?.classList.toggle("hidden", !isLoading);
      }

      watchVariantChange() {
        if (typeof subscribe !== "function") return;

        subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (data?.sectionId && data.sectionId !== this.sectionId) return;

          this.updatePrice();
          this.updateMedia(data?.variant);
          this.updatePurchaseType();
        });
      }

      /*
        Prefer the subscription widget's own price — otherwise the bar shows the
        one-time price while the customer has a discounted subscription
        selected, which is worse than showing no price at all.

        With no widget, clone the live theme price block rather than formatting
        money in JS: variants.js has already updated it before variantChange
        publishes, so sale formatting and the currency-code setting come along
        for free.
      */
      updatePrice() {
        if (!this.priceTarget) return;

        const selection = this.recurpaySelection();

        if (selection?.price) {
          /* Built as nodes, not innerHTML — this text comes from the app's DOM */
          const wrapper = document.createElement("span");
          wrapper.className = "sticky-atc__price--mirrored";

          const current = document.createElement("span");
          current.className = "sticky-atc__price-current";
          current.textContent = selection.price;
          wrapper.appendChild(current);

          if (selection.comparePrice) {
            const compare = document.createElement("s");
            compare.className = "sticky-atc__price-compare";
            compare.textContent = selection.comparePrice;
            wrapper.appendChild(compare);
          }

          this.priceTarget.replaceChildren(wrapper);
          return;
        }

        const livePrice = document.getElementById(`price-${this.sectionId}`);
        if (livePrice) this.priceTarget.innerHTML = livePrice.innerHTML;
      }

      updateMedia(variant) {
        const image = this.mediaTarget?.querySelector("img");
        if (!image || !variant?.featured_image?.src) return;

        const src = variant.featured_image.src;
        image.src = `${src}${src.includes("?") ? "&" : "?"}width=144`;
        image.removeAttribute("srcset");
        if (variant.featured_image.alt) image.alt = variant.featured_image.alt;
      }

      /*
        Purchase type. Feature-detected at runtime, in priority order:
        1. A native Shopify selling plan input on the form.
        2. Line-item property inputs, for apps running in legacy mode.
        3. Neither — hide the line and leave the rest of the bar working.
      */
      currentSellingPlanId() {
        const input = this.productForm.querySelector(
          '[name="selling_plan"]:not([disabled])',
        );
        if (!input) return null;

        if (input.type === "radio" || input.type === "checkbox") {
          const checked = this.productForm.querySelector(
            '[name="selling_plan"]:checked',
          );
          return checked?.value || null;
        }

        return input.value || null;
      }

      /* Label and price always move together — the widget drives both */
      refreshSelection() {
        this.updatePurchaseType();
        this.updatePrice();
      }

      purchaseSignature() {
        const selection = this.recurpaySelection();
        return [
          this.currentSellingPlanId(),
          selection?.title,
          selection?.price,
        ].join("|");
      }

      /*
        Apps in legacy mode carry the plan on line-item properties. These are
        hidden inputs, so :checked never matches them — read the value directly.
      */
      legacyPurchaseLabel() {
        const named = this.productForm.querySelector(
          'input[name="properties[_Plan]"]',
        );
        if (named?.value) return named.value;

        const inputs = Array.from(
          this.productForm.querySelectorAll('input[name^="properties"]'),
        ).filter((input) => {
          if (input.type === "radio" || input.type === "checkbox") {
            return input.checked;
          }
          return !input.disabled && input.value;
        });

        /* Skip internal id/data fields — they aren't customer-facing labels */
        const label = inputs.find(
          (input) => !/id|data/i.test(input.name) && input.value,
        );
        return label?.value || null;
      }

      /*
        RecurPay's own selected option, verified against the live widget markup:
          .recurpay__widget_container[--selected]
            .recurpay__group_input        radio, "subscription" | "one-time"
            .recurpay__card_title         "Every 3 Months Subscription"
            .recurpay__group_selling_price  price actually charged
            .recurpay__group_compare_price  struck-through was-price

        Reading the widget's rendered title and price is preferable to deriving
        them: the wording is already in the merchant's language, and the price
        includes the subscription discount without any maths or money
        formatting on our side.
      */
      recurpaySelection() {
        const containers = document.querySelectorAll(
          ".recurpay__widget_container",
        );
        if (!containers.length) return null;

        const selected =
          document.querySelector(
            ".recurpay__widget_container--selected",
          ) ||
          Array.from(containers).find(
            (c) => c.querySelector(".recurpay__group_input")?.checked,
          );
        if (!selected) return null;

        const text = (selector) => {
          const el = selected.querySelector(selector);
          const value = el?.textContent.trim().replace(/\s+/g, " ");
          return value || null;
        };

        return {
          title: text(".recurpay__card_title"),
          price: text(".recurpay__group_selling_price"),
          comparePrice: text(".recurpay__group_compare_price"),
        };
      }

      /*
        Last resort when the plan id isn't in the Liquid-rendered map — read the
        label the widget itself is showing for the selected option.
      */
      widgetLabelForPlan(planId) {
        const input = this.productForm.querySelector(
          `[name="selling_plan"][value="${planId}"]`,
        );

        const labelled =
          input?.closest("label") ||
          (input?.id ? document.querySelector(`label[for="${input.id}"]`) : null);
        if (labelled) return labelled.textContent.trim().replace(/\s+/g, " ");

        const option = this.productForm.querySelector(
          `option[value="${planId}"]`,
        );
        return option ? option.textContent.trim() : null;
      }

      /*
        Which tag text applies follows the selected purchase type. The widget's
        selected card is the primary signal; without a widget, any selling plan
        or legacy plan property means subscription, otherwise one-time.
      */
      updateTag() {
        if (!this.tagTarget) return;

        const selected = document.querySelector(
          ".recurpay__widget_container--selected",
        );
        const isSubscription = selected
          ? selected.dataset.type === "subscription-purchase"
          : Boolean(this.currentSellingPlanId() || this.legacyPurchaseLabel());

        const text = isSubscription
          ? this.tagTarget.dataset.tagSubscription
          : this.tagTarget.dataset.tagOnetime;

        if (text) {
          this.tagTarget.textContent = text;
          this.tagTarget.hidden = false;
        } else {
          this.tagTarget.hidden = true;
        }
      }

      updatePurchaseType() {
        /* Before the purchase-type guard — the tag works without that row */
        this.updateTag();

        if (!this.purchaseType || !this.purchaseTypeLabel) return;

        let label = this.recurpaySelection()?.title || null;

        if (!label) {
          const planId = this.currentSellingPlanId();
          if (planId) {
            const plan = this.sellingPlans[planId];
            label =
              (plan && (plan.name || plan.group)) ||
              this.widgetLabelForPlan(planId);
          } else {
            label = this.legacyPurchaseLabel();
          }
        }

        if (label) {
          this.purchaseTypeLabel.textContent = label;
          this.purchaseType.hidden = false;
        } else {
          this.purchaseType.hidden = true;
        }
      }

      /*
        The subscription widget is an app embed injected after load, so nothing
        about its markup can be relied on. Detection is therefore driven by the
        form's selling_plan input (which the app must set for checkout to work)
        rather than by the widget's own classes.

        A short poll backs up the event listeners because an app setting
        input.value programmatically fires no event and mutates no attribute,
        so neither a change listener nor a MutationObserver would see it.
      */
      watchPurchaseType() {
        this.updatePurchaseType();

        /* Fast path: catch interactions wherever the widget ended up */
        document.addEventListener("change", () => this.refreshSelection(), {
          capture: true,
        });

        /*
          Backstop: compare a composite signature so a frequency change that
          leaves selling_plan untouched is still caught.
        */
        this.lastSignature = this.purchaseSignature();
        this.pollTimer = setInterval(() => {
          if (document.hidden) return;
          const signature = this.purchaseSignature();
          if (signature !== this.lastSignature) {
            this.lastSignature = signature;
            this.updatePurchaseType();
            this.updatePrice();
          }
        }, 400);

        this.resolveWidget();

        /* Widget may not exist yet; stop looking once found */
        let pending = false;
        this.widgetObserver = new MutationObserver(() => {
          if (pending) return;
          pending = true;
          requestAnimationFrame(() => {
            pending = false;
            this.refreshSelection();
            this.resolveWidget();
          });
        });
        this.widgetObserver.observe(document.body, {
          childList: true,
          subtree: true,
        });

        this.purchaseType?.addEventListener("click", () => {
          const target = this.resolveWidget() || this.productForm;
          target.scrollIntoView({ behavior: "smooth", block: "center" });
        });
      }

      /*
        Scroll target only — the label itself never depends on this succeeding.
        Substring, case-insensitive attribute matching rather than exact class
        names, then a fall back to whatever contains the selling_plan input.
      */
      resolveWidget() {
        if (this.widget) return this.widget;

        this.widget =
          document.querySelector(
            '.recurpay-pdp-widget, [data-recurpay], [class*="recurpay" i], [id*="recurpay" i]',
          ) ||
          this.productForm
            .querySelector('[name="selling_plan"]')
            ?.closest("div") ||
          null;

        if (!this.widget) return null;

        this.widgetObserver?.disconnect();
        this.widgetObserver?.observe(this.widget, {
          childList: true,
          subtree: true,
          attributes: true,
        });

        return this.widget;
      }

      /*
        Always visible on mobile by default — the add-to-cart must never be more
        than a tap away. Opt in to hiding it while the main form or the footer is
        on screen via the "Always show sticky bar" setting.
      */
      initVisibility() {
        if (this.alwaysVisible) {
          this.classList.add(this.activeClass);
          return;
        }

        const watched = document.querySelectorAll(
          ".wt-product__add-to-cart, .wt-footer",
        );

        if (!watched.length) {
          this.classList.add(this.activeClass);
          return;
        }

        this.classList.add(this.activeClass);

        this.observer = new IntersectionObserver((entries) => {
          entries.forEach(({ isIntersecting, target }) => {
            isIntersecting
              ? this.intersecting.push(target)
              : (this.intersecting = this.intersecting.filter(
                  (item) => item !== target,
                ));
          });

          this.classList.toggle(this.activeClass, !this.intersecting.length);
        });

        watched.forEach((item) => this.observer.observe(item));
      }
    },
  );
}
